
import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from './AuthBridge';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { 
  X, CalendarDays, Sparkles, Loader2, Target, CheckCircle2, 
  Circle, ChevronRight, GraduationCap, ClipboardList, 
  Zap, Brain, Beaker, Layers, BookOpen, AlertCircle, RefreshCw, Save,
  ShieldCheck
} from 'lucide-react';
import { StudyPlan, StudyPlanTask } from '../types';

interface StudyPlannerOverlayProps {
  onClose: () => void;
}

export const StudyPlannerOverlay: React.FC<StudyPlannerOverlayProps> = ({ onClose }) => {
  const { user, activeCert, updateUser } = useAuth();
  const tokens = useThemeTokens();
  const [targetDate, setTargetDate] = useState(user?.studyPlan?.targetDate || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [localPlan, setLocalPlan] = useState<StudyPlan | null>(user?.studyPlan || null);

  // VIEWPORT LOCKING & RESET PROTOCOL
  useEffect(() => {
    // 1. Reset viewport to top so modal is visible immediately
    window.scrollTo(0, 0);
    
    // 2. Lock body scroll to prevent background movement
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
        // Restore scroll on unmount
        document.body.style.overflow = originalStyle;
    };
  }, []);

  const generatePlan = async () => {
    if (!targetDate) return;
    setIsGenerating(true);
    try {
      const response = await fetch('/practice-exams/api/study-planner/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          targetDate, 
          certName: activeCert?.name || 'CNA',
          certId: activeCert?.id || 'cna'
        })
      });
      const data = await response.json();
      
      // Resolve tasks from either top-level or nested studyPlan key
      const rawTasks = data.tasks || data.studyPlan?.tasks || [];
      
      // Map and ensure stable IDs and fields
      const processedTasks: StudyPlanTask[] = rawTasks.map((t: any, idx: number) => ({
          id: t.id || `task-${Date.now()}-${idx}`,
          title: t.title || 'Untitled Objective',
          type: t.type || 'study',
          dueDate: t.dueDate || t.date || targetDate,
          completed: !!t.completed
      }));

      setLocalPlan({
          ...data,
          tasks: processedTasks,
          isInitialized: true,
          targetDate: data.targetDate || targetDate
      });
    } catch (e) {
      console.error("Planner Logic Sync Error:", e);
      alert("AI Synthesis Failed. Please check the clinical bridge.");
    } finally {
      setIsGenerating(false);
    }
  };

  const savePlan = async (plan: StudyPlan) => {
    try {
      await updateUser({ studyPlan: plan });
      onClose();
    } catch (e) {
      alert("Failed to commit plan to registry.");
    }
  };

  const toggleTask = (taskId: string) => {
    if (!localPlan) return;
    const nextTasks = (localPlan.tasks || []).map(t => 
      t.id === taskId ? { ...t, completed: !t.completed } : t
    );
    const updatedPlan = { ...localPlan, tasks: nextTasks };
    setLocalPlan(updatedPlan);
    
    if (user?.studyPlan?.isInitialized) {
        updateUser({ studyPlan: updatedPlan });
    }
  };

  const progress = useMemo(() => {
    const tasks = localPlan?.tasks || [];
    if (tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.completed).length;
    return Math.round((completed / tasks.length) * 100);
  }, [localPlan]);

  const tasksByDate = useMemo<Record<string, StudyPlanTask[]>>(() => {
    const tasks = localPlan?.tasks || [];
    const groups: Record<string, StudyPlanTask[]> = {};
    tasks.forEach(t => {
      const date = t.dueDate;
      if (!groups[date]) groups[date] = [];
      groups[date].push(t);
    });
    return groups;
  }, [localPlan]);

  const getTaskIcon = (type: string) => {
    switch(type) {
      case 'guide': return BookOpen;
      case 'skills': return Layers;
      case 'quiz': return ClipboardList;
      case 'exam': return GraduationCap;
      case 'scenario': return Brain;
      case 'simulator': return Beaker;
      default: return Zap;
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-950/80 backdrop-blur-xl flex items-start justify-center p-3 md:p-6 pt-6 md:pt-16 animate-fade-in overflow-y-auto custom-scrollbar">
      <div className={`rounded-[2rem] md:rounded-[3rem] shadow-2xl w-full max-w-4xl h-auto max-h-[92dvh] md:max-h-[85vh] flex flex-col animate-slide-up overflow-hidden border ${
        tokens.isHighContrast 
          ? 'bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]' 
          : 'bg-white dark:bg-slate-900 border-white/10 dark:border-slate-800'
      }`}>
        
        <div className={`p-4 md:p-6 border-b flex justify-between items-center shrink-0 ${
          tokens.isClinical ? 'bg-[#0B2942] text-white border-slate-800' :
          tokens.isHighContrast ? 'bg-amber-300 text-black border-b-2 border-black' :
          tokens.isVibrant ? 'bg-gradient-to-r from-violet-950 to-indigo-950 text-white border-violet-900/50' :
          'bg-slate-50/50 dark:bg-slate-950/50 border-slate-100 dark:border-slate-800'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl shadow-lg ${tokens.iconBg}`}>
              <CalendarDays size={18} />
            </div>
            <div>
              <h2 className={`text-base md:text-xl font-black tracking-tight leading-none ${tokens.isHighContrast ? 'text-black' : tokens.isClinical || tokens.isVibrant ? 'text-white' : 'text-slate-900 dark:text-white'}`}>Study Architect</h2>
              <p className={`text-[7px] md:text-[8px] font-black tracking-widest mt-0.5 ${tokens.isHighContrast ? 'text-black/80' : tokens.isClinical || tokens.isVibrant ? 'text-slate-300' : 'text-slate-400'}`}>Registry Pace Controller</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
             {localPlan?.isInitialized && (
               <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl border border-emerald-100 dark:border-emerald-800">
                 <span className="text-[9px] font-black text-emerald-700 dark:text-emerald-300 tracking-tight">{progress}% READY</span>
                 <div className="w-16 h-1.5 bg-emerald-200 dark:bg-emerald-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-600 dark:bg-emerald-400 transition-all duration-1000" style={{ width: `${progress}%` }}></div>
                 </div>
               </div>
             )}
             <button onClick={onClose} className="p-2 bg-white/10 dark:bg-slate-800 text-slate-400 hover:text-rose-500 rounded-xl transition-all shadow-sm border border-slate-100 dark:border-slate-800 active:scale-90"><X size={20}/></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-8">
          {!localPlan?.isInitialized ? (
            <div className="max-w-2xl mx-auto space-y-4 md:space-y-8 py-4 md:py-8 text-center animate-pop">
                <div className="space-y-2">
                    <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl md:rounded-[2.5rem] flex items-center justify-center mx-auto shadow-xl border ${tokens.badge}`}>
                        <Sparkles size={32} className="animate-pulse" />
                    </div>
                    <h3 className={`text-2xl md:text-4xl font-black tracking-tighter leading-none ${tokens.isHighContrast ? 'text-black' : 'text-slate-900 dark:text-white'}`}>Plan Your Victory</h3>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-xs md:text-base max-w-md mx-auto opacity-95">"Calibrate your timeline for clinical mastery."</p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-950/60 p-6 md:p-10 rounded-2xl md:rounded-[3rem] border border-slate-100 dark:border-slate-800 space-y-6 md:space-y-8 shadow-inner">
                    <div className="space-y-2 text-left">
                        <label className="text-[8px] md:text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Target size={12} className={tokens.accentText}/> Target Board Date</label>
                        <input 
                            type="date" 
                            value={targetDate} 
                            onChange={e => setTargetDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full p-4 md:p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl md:rounded-[2rem] font-black text-xl md:text-2xl text-slate-900 dark:text-white outline-none focus:ring-4 focus:ring-indigo-500/10 shadow-lg"
                        />
                    </div>

                    <button 
                        onClick={generatePlan}
                        disabled={isGenerating || !targetDate}
                        className={`w-full py-5 md:py-6 rounded-xl md:rounded-[2rem] font-black text-xs md:text-sm tracking-widest shadow-2xl transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3 ${tokens.primaryBtn}`}
                    >
                        {isGenerating ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                        {isGenerating ? 'Synthesizing...' : 'Generate AI Study Plan'}
                    </button>
                    
                    <div className="flex items-center gap-3 justify-center opacity-40">
                         <div className="h-px flex-1 bg-slate-300 dark:bg-slate-700"></div>
                         <p className="text-[6px] md:text-[8px] font-black text-slate-400 tracking-widest">Institutional Grade Schedule</p>
                         <div className="h-px flex-1 bg-slate-300 dark:bg-slate-700"></div>
                    </div>
                </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
                <div className="lg:col-span-8 space-y-4 md:space-y-6">
                    <div className="flex justify-between items-center px-1">
                        <h3 className="text-[8px] md:text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                            <ClipboardList size={12} className="text-indigo-600" /> Planned Objectives
                        </h3>
                        <button onClick={() => setLocalPlan(null)} className="text-[8px] md:text-[9px] font-black text-indigo-600 hover:underline flex items-center gap-1.5"><RefreshCw size={10}/> Reschedule</button>
                    </div>

                    <div className="space-y-6 md:space-y-8">
                        {(Object.entries(tasksByDate) as [string, StudyPlanTask[]][]).sort((a, b) => a[0].localeCompare(b[0])).map(([date, tasks]) => (
                            <div key={date} className="space-y-2 md:space-y-3 animate-slide-up">
                                <div className="flex items-center gap-3">
                                    <span className="px-2 py-0.5 bg-slate-900 text-white rounded text-[8px] md:text-[9px] font-black tabular-nums shadow-md">
                                        {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                    <div className="h-px flex-1 bg-slate-100"></div>
                                </div>
                                <div className="grid grid-cols-1 gap-2">
                                    {tasks.map(task => {
                                        const TaskIcon = getTaskIcon(task.type || 'study');
                                        return (
                                            <div 
                                                key={task.id} 
                                                onClick={() => toggleTask(task.id)}
                                                className={`p-3 md:p-4 rounded-xl md:rounded-2xl border-2 transition-all cursor-pointer group relative overflow-hidden ${task.completed ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-100 hover:border-indigo-200 hover:shadow-lg'}`}
                                            >
                                                <div className="relative z-10 flex items-center gap-3 md:gap-4">
                                                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center shrink-0 shadow-md transition-transform group-active:scale-90 ${task.completed ? 'bg-emerald-500 text-white' : 'bg-slate-50 text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600'}`}>
                                                        {task.completed ? <CheckCircle2 size={20} /> : <TaskIcon size={20} />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className={`text-xs md:text-base font-black tracking-tight truncate ${task.completed ? 'text-emerald-900' : 'text-slate-900'}`}>{task.title}</h4>
                                                        <p className={`text-[7px] md:text-[9px] font-bold tracking-widest mt-0.5 ${task.completed ? 'text-emerald-600' : 'text-slate-400'}`}>{(task.type || 'study').replace('_', ' ')} Phase</p>
                                                    </div>
                                                    {!task.completed ? (
                                                        <Circle size={18} className="text-slate-100 group-hover:text-indigo-200 transition-colors" />
                                                    ) : (
                                                        <CheckCircle2 size={18} className="text-emerald-600" />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-4 space-y-6">
                    <div className="bg-slate-900 rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-8 text-white relative overflow-hidden shadow-2xl group border border-white/5">
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:rotate-12 transition-transform duration-1000"><Target size={80}/></div>
                        <div className="relative z-10 space-y-6">
                            <div>
                                <h4 className="text-[8px] md:text-[10px] font-black text-indigo-400 tracking-widest mb-4">Board Timeline</h4>
                                <div className="flex items-center gap-6">
                                    <div className="text-center">
                                        <p className="text-2xl md:text-3xl font-black">{Object.keys(tasksByDate).length}</p>
                                        <p className="text-[7px] md:text-[8px] font-black text-slate-500 mt-1">Study Days</p>
                                    </div>
                                    <div className="w-px h-8 bg-white/10"></div>
                                    <div className="text-center">
                                        <p className="text-2xl md:text-3xl font-black text-indigo-400">{localPlan?.tasks?.length || 0}</p>
                                        <p className="text-[7px] md:text-[8px] font-black text-slate-500 mt-1">Total Tasks</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-3">
                                <div className="flex items-center gap-3">
                                    <GraduationCap size={16} className="text-indigo-400" />
                                    <div>
                                        <p className="text-[8px] md:text-[9px] font-black text-slate-400 leading-none">Target Goal</p>
                                        <p className="text-xs md:text-sm font-black text-white mt-1">{localPlan?.targetDate ? new Date(localPlan.targetDate).toLocaleDateString() : '---'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 md:p-8 bg-white border border-slate-200 rounded-[2.5rem] shadow-sm space-y-4">
                        <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2"><AlertCircle size={12}/> Protocol Guide</h4>
                        <div className="space-y-3">
                            <div className="flex gap-2">
                                <div className="w-1 h-1 rounded-full bg-emerald-500 mt-1.5 shrink-0"></div>
                                <p className="text-[8px] md:text-[10px] font-bold text-slate-600 leading-relaxed">Toggles sync to your account dashboard index.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          )}
        </div>

        {localPlan?.isInitialized && (
            <div className="p-4 md:p-6 border-t border-slate-100 bg-slate-50/50 flex flex-col md:flex-row justify-between items-center gap-4 shrink-0">
                <div className="hidden md:flex items-center gap-3">
                    <ShieldCheck size={18} className="text-emerald-500" />
                    <p className="text-[9px] font-black text-slate-400 tracking-widest">Digital Registry Signed</p>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <button onClick={onClose} className="flex-1 md:flex-none px-6 py-4 font-black text-[10px] text-slate-400 hover:text-slate-900 transition-colors">Discard</button>
                    <button 
                        onClick={() => savePlan(localPlan)}
                        className="flex-[2] md:flex-none px-8 py-4 bg-slate-900 text-white rounded-2xl font-black text-xs tracking-widest shadow-2xl hover:bg-black transition-all active:scale-95 border-b-4 border-black flex items-center justify-center gap-3"
                    >
                        <Save size={16} /> Synchronize Plan
                    </button>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};
